import { useState, useEffect } from 'react'
import { FaChartLine, FaHandshake, FaLightbulb } from 'react-icons/fa'
import NetworkGraph from './components/NetworkGraph'

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [calendlyLoaded, setCalendlyLoaded] = useState(false)

  useEffect(() => {
    const checkCalendly = setInterval(() => {
      if (window.Calendly) {
        setCalendlyLoaded(true)
        clearInterval(checkCalendly)
      }
    }, 100)
    
    return () => clearInterval(checkCalendly)
  }, [])

  const openCalendly = (e) => {
    e.preventDefault()
    if (calendlyLoaded) {
      window.Calendly.initPopupWidget({url: 'https://calendly.com/d/cwxr-597-hmx'})
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-primary-600">6degrees.ai</span>
            </div>
            <div className="hidden md:flex items-center">
              <a href="#" onClick={openCalendly} className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700">
                Book a Call
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 lg:mt-16 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block">Your Next Big Deal</span>
                  <span className="block text-primary-600">Is Already in Your Network</span>
                </h1>
                <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                  Enterprise sales teams using 6degrees.ai unlock 8x more warm introductions—directly from their CRM.
                </p>
                <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
                  <div className="rounded-md shadow">
                    <a href="#" onClick={openCalendly} className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 md:py-4 md:text-lg md:px-10">
                      Book a Call
                    </a>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Network Graph */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <NetworkGraph />
      </div>

      {/* Features Section */}
      <div id="features" className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Supercharge Your Sales Process
            </h2>
          </div>

          <div className="mt-10">
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-600 text-white">
                  <FaLightbulb className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-xl font-medium text-gray-900">Network Intelligence</h3>
                <p className="mt-2 text-center text-gray-500">
                  Analyze your team's professional networks to find hidden connections to potential customers.
                </p>
              </div>

              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-600 text-white">
                  <FaChartLine className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-xl font-medium text-gray-900">CRM Integration</h3>
                <p className="mt-2 text-center text-gray-500">
                  Seamlessly integrates with your existing CRM to enhance your sales data with relationship insights.
                </p>
              </div>

              <div className="flex flex-col items-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-600 text-white">
                  <FaHandshake className="h-6 w-6" />
                </div>
                <h3 className="mt-4 text-xl font-medium text-gray-900">Warm Introductions</h3>
                <p className="mt-2 text-center text-gray-500">
                  Get introduced to decision-makers through trusted mutual connections.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CRM Integration Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Seamless CRM Integration
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Access your network's power directly within your CRM
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16 items-center">
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Your Network, Right Where You Need It
                </h3>
                <p className="mt-4 text-lg text-gray-500">
                  For every contact in your CRM, instantly see who in your network can make warm introductions. Plus, discover which of your contacts they could help you reach.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-100 text-primary-600">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-900">Bi-directional Insights</h4>
                    <p className="mt-2 text-gray-500">
                      See both who can refer you to a contact and who that contact could introduce you to.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-100 text-primary-600">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-900">Real-time Updates</h4>
                    <p className="mt-2 text-gray-500">
                      Connection data stays fresh as your team's network grows and changes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-8">
              <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                <img 
                  src="https://i.imgur.com/ieVRs8O.png"
                  alt="CRM Contact View"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="bg-white rounded-lg shadow-xl overflow-hidden">
                <img 
                  src="https://i.imgur.com/Xd6IUBM.png"
                  alt="CRM Referrer View"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Booking Stats Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Real Results from Real Teams
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              See how teams are transforming their sales pipeline with warm introductions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-primary-600 mb-4">1,000+</div>
              <p className="text-lg text-gray-700">High-quality referrers identified per 5-rep team</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-primary-600 mb-4">1 ask</div>
              <p className="text-lg text-gray-700">Per referrer per month, maintaining strong relationships</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-primary-600 mb-4">20%</div>
              <p className="text-lg text-gray-700">Conversion rate from ask to qualified demo</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8 text-center">
              <div className="text-4xl font-bold text-primary-600 mb-4">200+</div>
              <p className="text-lg text-gray-700">Monthly demos from warm introductions</p>
            </div>
          </div>

          <div className="mt-12 bg-primary-50 rounded-xl p-8">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-lg text-gray-700">
                Our systematic approach ensures each referrer receives exactly 1 ask per month, respecting their time while maximizing opportunities. With a 20% conversion rate from ask to qualified demo, teams consistently generate 200+ monthly demos—all backed by the power of trusted relationships.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Cold Outreach Stats Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900">
              The Data is Clear—Cold Outreach is Being Left Behind
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Cold Outreach Problems */}
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 text-red-500 text-2xl">❌</div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">60% Reduction in Cold Outreach</p>
                  <p className="mt-2 text-gray-600">Over 60% of businesses are reducing their reliance on cold calls and emails.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 text-red-500 text-2xl">❌</div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">Trust Gap</p>
                  <p className="mt-2 text-gray-600">Consumers trust recommendations 92% more than unsolicited outreach.</p>
                </div>
              </div>
            </div>

            {/* Relationship-Driven Success */}
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 text-green-500 text-2xl">✅</div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">3X Higher Conversion</p>
                  <p className="mt-2 text-gray-600">Companies see 3X higher conversion rates than traditional outreach when using warm introductions.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 text-green-500 text-2xl">✅</div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">2X Faster Sales Cycles</p>
                  <p className="mt-2 text-gray-600">Sales cycles are 2X faster because referrals open doors instantly.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-600">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to boost your sales?</span>
            <span className="block text-primary-100">Find warm intros today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <a href="#" onClick={openCalendly} className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50">
                Book a Call
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="mt-8 border-t border-gray-200 pt-8 md:flex md:items-center md:justify-between">
            <p className="mt-8 text-base text-gray-400 md:mt-0 md:order-1">
              © 2024 6degrees.ai. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App